import React from "react";
import { useDispatch } from "react-redux";
import { Box, Typography } from "@mui/material";
import moment from "moment-timezone";
import { getStockSummaryReports } from "../../api/reports";
import Filters from "./Filters";
import { notificationAdd } from "../../actions/notification";
import List, { EmptyView, ListActions, Menu, MenuItem } from "../../components/OList";
import { t, tl } from "../../components/translate";
import { downloadExcel } from "../../helpers/files";
import { getValue } from "../../helpers/formatters";
import { formatDataForExcel, formatHeaderForExcel } from "../accounts/Reports/helper";
import { ProductType } from "../Stock/StockCreateEdit/CreateStockProduct";
import { endOfMonth, startOfMonth } from "../../components/Calendar/functions/calendarFunctions";
import { rupeeDisplay } from "../../helpers/rupee";
import { errorFetchMessage, MODULE } from "../../helpers/messages";
import useMobileScreen from "../../hooks/useMobileScreen";
import useIsReactNativeWebView from "../../hooks/useIsReactNativeWebView";

export interface StockSummaryData {
  date: string;
  name: string;
  productId: number;
  batchId: string;
  openingUnit: number;
  stockIn: number;
  stockOut: number;
  supplierName: string;
  supplierId: number;
  avgPurchasePrice: number;
  unit: string;
  package: string;
  balanceUnit: number;
  stockValue: number;
  totalStockValue?: number;
  expiryDate: string;
  unitPriceExcVAT: number;
  unitPriceIncVAT: number;
}

const docColumns = [
  "Product Name",
  "Supplier Name",
  "Batch No.",
  "Unit/Package",
  "Expiry Date",
  "Gross Selling Price",
  "Net Selling Price",
  "Average Purchase Price",
  "Opening Units",
  "Stock In Units",
  "Stock Out Units",
  "Balance Units",
  "Stock Value",
  "Total Stock Value"
];

const docRowProcessor = ({
  name,
  supplierName,
  batchId,
  unit,
  package: p,
  avgPurchasePrice,
  openingUnit,
  expiryDate,
  unitPriceExcVAT,
  unitPriceIncVAT,
  stockOut,
  stockIn,
  balanceUnit,
  stockValue,
  totalStockValue,
  // eslint-disable-next-line camelcase
  __meta__row_type
}: // eslint-disable-next-line camelcase
Partial<StockSummaryData & { __meta__row_type: string }>) => {
  // eslint-disable-next-line camelcase
  if (__meta__row_type === "segment_summary") return;
  // eslint-disable-next-line consistent-return
  return [
    name,
    supplierName,
    batchId,
    `${unit}/${p}`,
    moment(expiryDate).format("YYYY-MM-DD"),
    unitPriceExcVAT,
    unitPriceIncVAT,
    avgPurchasePrice,
    openingUnit,
    stockIn,
    stockOut,
    balanceUnit,
    stockValue,
    totalStockValue
  ].map((item) => getValue(item));
};

const StockSummaryReport = (): React.ReactNode => {
  const dispatch = useDispatch();
  const isMobileScreen = useMobileScreen();
  const { isRnWebView } = useIsReactNativeWebView();

  // local states
  const [isRequestLoading, setIsRequestLoading] = React.useState(false);
  const [stockSummaryData, setStockSummaryData] = React.useState<StockSummaryData[]>([]);
  const [filters, setFilters] = React.useState({
    from: startOfMonth(new Date()).toDate(),
    until: endOfMonth(new Date()).toDate(),
    productItems: [],
    supplierIds: [],
    productType: ProductType.Sellable
  });

  // memoized values
  const totalStockValue = rupeeDisplay(
    Math.floor(
      stockSummaryData.reduce(
        (prev, cur) =>
          prev + cur.avgPurchasePrice * ((+cur.openingUnit || 0) + +cur.stockIn - +cur.stockOut),
        0
      ) || 0
    )
  );

  // effects
  React.useEffect(() => {
    (async () => {
      try {
        setIsRequestLoading(true);
        const response = await getStockSummaryReports(filters);
        setStockSummaryData(response?.map((item, index) => ({ ...item, id: index })));
      } catch (error: any) {
        dispatch(
          notificationAdd({
            id: new Date().getTime(),
            variant: "error",
            message: error?.data?.message || errorFetchMessage(MODULE.STOCK_SUMMARY_REPORT),
            autoTimeout: true
          })
        );
      } finally {
        setIsRequestLoading(false);
      }
    })();
  }, [filters, dispatch]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* filter */}
      <Box sx={{ px: 3, py: 2 }}>
        <Filters
          filters={filters}
          onSetFilters={(newFilters) => setFilters(newFilters)}
          showMonthOnly
          disableMonthsBefore
          isRequestLoading={isRequestLoading}
        />
      </Box>

      {/* list */}
      <Box
        className="stockSummaryReport"
        sx={{
          borderTop: 1,
          borderColor: "divider",
          width: "100%",
          overflow: "auto"
        }}
      >
        <Box sx={{ minWidth: isMobileScreen || isRnWebView ? "1300px" : "auto", mb: "40px" }}>
          <List
            automation="stockCashFlowList"
            data={stockSummaryData}
            rowHeight={50}
            adjustHeightToContents
            defaultSortColumn="name"
            columns={[
              {
                key: "name",
                label: tl("reports.stock.productName"),
                segmentable: true,
                segmentBy: ({ name }) => name,
                sortable: true,
                formatter: ({ name }) => <Typography>{name}</Typography>
              },
              {
                key: "supplierName",
                label: "Supplier",
                sortable: true,
                formatter: ({ supplierName }) => <Typography>{supplierName}</Typography>
              },
              {
                key: "batchId",
                label: tl("reports.batchId"),
                sortable: true,
                formatter: ({ batchId }) => <Typography>{batchId}</Typography>
              },
              {
                key: "unitPackage",
                label: "Unit/Package",
                formatter: ({ unit = "", package: p = "" }) => (
                  <Typography>{`${unit}/${p}`}</Typography>
                )
              },
              {
                key: "openingUnit",
                label: "Opening Unit ",
                sortable: true,
                formatter: ({ openingUnit }) => <Typography>{openingUnit}</Typography>
              },
              {
                key: "stockIn",
                label: "Stock In",
                sortable: true,
                formatter: ({ stockIn }) => <Typography>{stockIn}</Typography>
              },
              {
                key: "stockOut",
                label: "Stock Out",
                sortable: true,
                formatter: ({ stockOut }) => <Typography>{stockOut}</Typography>
              },
              {
                key: "balanceUnit",
                label: "Balance Unit",
                sortable: true,
                formatter: ({ balanceUnit }) => <Typography>{balanceUnit}</Typography>
              },
              {
                key: "avgPurchasePrice",
                label: "Purchase Price",
                sortable: true,
                formatter: ({ avgPurchasePrice }) => (
                  <Typography>{(avgPurchasePrice || 0).toFixed(2)}</Typography>
                )
              },
              {
                key: "stockValue",
                label: "Stock Value",
                sortable: true,
                formatter: ({ stockValue }) => (
                  <Typography>{(stockValue || 0).toFixed(2)}</Typography>
                )
              }
            ]}
            segementSummaryRenderer={(acc) => (
              <Box
                sx={{ background: "#e6e6e6" }}
                display="flex"
                alignItems="center"
                flexGrow={1}
                className="segment-wrapper"
              >
                <Typography ml="20px" fontWeight={500} className="name okhati-list-rowcell">
                  {acc.segment}
                </Typography>
                <span />
                <span />
                <span />
                <Typography className="openingUnit">
                  {acc.rows.reduce((prev, cur) => prev + cur.openingUnit, 0)}
                </Typography>
                <Typography className="stockIn">
                  {acc.rows.reduce((prev, cur) => prev + cur.stockIn, 0)}
                </Typography>
                <Typography className="stockOut">
                  {acc.rows.reduce((prev, cur) => prev + cur.stockOut, 0)}
                </Typography>
                <Typography className="balanceUnit">
                  {acc.rows.reduce((prev, cur) => prev + cur.balanceUnit, 0)}
                </Typography>
                <span />
                <Typography className="stockValue">
                  {rupeeDisplay(
                    Math.floor(acc.rows.reduce((prev, cur) => prev + cur.stockValue, 0) || 0)
                  )}
                </Typography>
              </Box>
            )}
          >
            <EmptyView>
              <Box textAlign="center" padding="50px">
                No items to show.
              </Box>
            </EmptyView>
            <ListActions>
              {({ getProcessedData }) => (
                <Menu>
                  <MenuItem
                    onClick={async () => {
                      try {
                        const formatedData = formatDataForExcel(
                          getProcessedData(),
                          docRowProcessor
                        );
                        formatedData[0][9] = { value: totalStockValue };
                        await downloadExcel(
                          "Stock Summary Report",
                          formatHeaderForExcel(docColumns),
                          formatedData
                        );
                      } catch (err) {
                        dispatch(
                          notificationAdd({
                            id: new Date().getUTCMilliseconds(),
                            variant: "error",
                            message: "Failed to download Excel.",
                            autoTimeout: true
                          })
                        );
                      }
                    }}
                  >
                    {tl("reports.excel")}
                  </MenuItem>
                </Menu>
              )}
            </ListActions>
          </List>
          <Box className="totalBar">
            <Box display="flex" paddingLeft="20px">
              {t("reports.total")}
            </Box>
            <Box display="flex" flexBasis="192px" paddingRight="26px" justifyContent="flex-end">
              <Typography>{totalStockValue}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StockSummaryReport;
